import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { LIFECYCLE_FIELD_DESCRIPTORS } from './_lifecycleFields.js';
import { VERSION_FIELD_DESCRIPTOR } from './_versionField.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { PRODUCT } from '../types/product.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for ProductData entity.
 * Stored in 'org/{orgId}/product'.
 *
 * Fields without groups are always persisted.
 * Fields with groups are persisted only when those groups are active.
 */
export const productDescriptor = {
    name: PRODUCT,
    label: 'Produto',
    domain: 'catalog',
    collectionPath: 'org/{orgId}/product',
    version: 1,
    description: 'Represents a product in the catalog with code, reference, description, unit, and other attributes',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        // NoCache: entidade versionada (spec 0045) — getById em cache devolve versão velha e anula a detecção de conflito.
        cache: CacheStrategy.NoCache,
        groups: ['textil', 'variations'],
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            // No groups = always persisted
            description: 'Unique product identifier',
        },
        {
            name: 'code',
            example: 8842,
            label: 'Código',
            type: 'number',
            required: true,
            unique: true,
            autoNumber: true,
            description: 'Numeric product code (catalog identifier; non-negative, capped at MAX_CODE_LARGE)',
        },
        {
            name: 'reference',
            example: 'ART-8842',
            label: 'Referência',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.CODE,
            description: 'Product reference / SKU',
        },
        {
            name: 'barcode',
            example: '7891234567895',
            label: 'Código de barras',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.BARCODE,
            // Identifica o item (GTIN) — não se copia; unicidade não é aplicada hoje.
            copy: false,
            description: 'Barcode / GTIN (EAN-13, UPC, Code 128, etc.) for scanning and label printing',
        },
        {
            name: 'ncm',
            example: '60063200',
            label: 'NCM',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NCM,
            description: 'NCM (Nomenclatura Comum do Mercosul) — digits-only; looked up against ref.ncm (0396); invalid codes warn only',
        },
        {
            name: 'description',
            example: 'Malha PV 30/1 Penteada',
            label: 'Descrição',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.NAME,
            description: 'Product description',
        },
        {
            name: 'unit',
            example: 'KG',
            label: 'Unidade',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.UNIT,
            description: 'Unit of measure (e.g., "UN", "KG", "M", "L") — free string, not an FK',
        },
        {
            name: 'quantityDecimals',
            example: 3,
            label: 'Casas decimais da quantidade',
            type: 'number',
            required: false,
            description: 'Decimal places accepted for this product quantity (0–4). Runtime authority; default at edit time comes from Unit of the same code (spec 0032 §3)',
        },
        {
            name: 'salePrice',
            example: 38.9,
            label: 'Valor de venda',
            type: 'number',
            required: false,
            description: 'Sale / list price',
        },
        {
            name: 'purchasePrice',
            example: 22.5,
            label: 'Valor de compra',
            type: 'number',
            required: false,
            description: 'Purchase / cost price',
        },
        {
            name: 'unitWeight',
            example: 0.18,
            label: 'Peso unitário',
            type: 'number',
            required: false,
            description: 'Unit weight',
        },
        {
            name: 'grossWeight',
            example: 0.185,
            label: 'Peso bruto',
            type: 'number',
            required: false,
            description: 'Gross weight',
        },
        {
            name: 'productGroupId',
            label: 'Grupo de produto',
            type: 'string',
            required: false,
            description: 'Reference to the product group this product belongs to',
        },
        {
            name: 'brandId',
            label: 'Marca',
            type: 'string',
            required: false,
            description: 'Reference to the brand',
        },
        {
            name: 'defaultSupplierId',
            label: 'Fornecedor padrão',
            type: 'string',
            required: false,
            description: 'Default supplier (Party with SUPPLIER role)',
        },
        {
            name: 'imageUrl',
            example: 'https://cdn.valeverde.example/products/art-8842.jpg',
            label: 'Imagem',
            type: 'string',
            required: false,
            // Spec 0051 D17 — file belongs to the source record (storage cleanup on change).
            copy: false,
            description: 'Main product image URL',
        },
        {
            name: 'galleryUrls',
            label: 'Galeria',
            type: 'array',
            required: false,
            // Spec 0051 D17 — file belongs to the source record.
            copy: false,
            description: 'Product gallery image URLs',
        },
        {
            name: 'grammage',
            example: 180,
            label: 'Gramatura (g/m²)',
            type: 'number',
            required: false,
            groups: ['textil'],
            description: 'Fabric weight per square meter in grams',
        },
        {
            name: 'entryWidth',
            example: 1.8,
            label: 'Largura de entrada (m)',
            type: 'number',
            required: false,
            groups: ['textil'],
            description: 'Fabric width at entry in meters',
        },
        {
            name: 'exitWidth',
            example: 1.75,
            label: 'Largura de saída (m)',
            type: 'number',
            required: false,
            groups: ['textil'],
            description: 'Fabric width at exit in meters',
        },
        {
            name: 'productionVariationColor',
            example: true,
            label: 'Cor',
            type: 'boolean',
            required: false,
            groups: ['variations'],
            description: 'Product may vary by color in production',
        },
        {
            name: 'acceptedVariationOptionIds',
            label: 'Opções de variação aceitas',
            type: 'array',
            required: false,
            groups: ['variations'],
            description: 'VariationOption ids accepted as OPTION-axis variations for this product',
        },
        {
            name: 'sizeGridId',
            label: 'Grade de tamanhos',
            type: 'string',
            required: false,
            groups: ['variations'],
            description: 'When set, the product is produced/sold on a size grid (SIZE axis)',
        },
        {
            name: 'acceptedSizeOccurrenceIds',
            label: 'Tamanhos aceitos',
            type: 'array',
            required: false,
            groups: ['variations'],
            description: 'Subset of SizeOccurrence ids accepted for this product. Empty/absent = all sizes of the grid (E3)',
        },
        {
            name: 'producedViaSheet',
            example: true,
            label: 'Produzido via ficha técnica',
            type: 'boolean',
            required: false,
            groups: ['variations'],
            description: 'When true, variations resolve a technical sheet recipe; when false/absent, variation is identity only (imported/resold product)',
        },
        {
            name: 'stockControlEnabled',
            example: true,
            label: 'Controlar estoque deste produto',
            type: 'boolean',
            required: false,
            description: 'Master switch for product-level stock control (org stockControlEnabled is the ceiling)',
        },
        {
            name: 'stockControlByLot',
            example: true,
            label: 'Controlar por lote',
            type: 'boolean',
            required: false,
            description: 'Lot control for this product; requires stockControlEnabled; org lotControl is the ceiling',
        },
        {
            name: 'stockHasExpiry',
            example: true,
            label: 'Produto tem validade',
            type: 'boolean',
            required: false,
            description: 'When true, lot expiryDate is required (fase C); requires stockControlByLot',
        },
        {
            name: 'stockControlByVolume',
            example: true,
            label: 'Controlar por volume',
            type: 'boolean',
            required: false,
            description: 'Volume / handling-unit control (fase E); requires stockControlEnabled',
        },
        ...LIFECYCLE_FIELD_DESCRIPTORS,
        ...AUDIT_FIELD_DESCRIPTORS,
        VERSION_FIELD_DESCRIPTOR,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'productGroupId',
            targetEntity: 'ProductGroup',
            relationship: 'many-to-one',
            description: 'The product group this product belongs to',
            displayField: 'pathDescription',
        },
        {
            fieldName: 'brandId',
            targetEntity: 'Brand',
            relationship: 'many-to-one',
            description: 'The brand of the product',
            displayField: 'description',
        },
        {
            fieldName: 'defaultSupplierId',
            targetEntity: 'Party',
            relationship: 'many-to-one',
            displayField: 'displayName',
            description: 'Default supplier party',
        },
        {
            fieldName: 'sizeGridId',
            targetEntity: 'SizeGrid',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Size grid used by this product (SIZE axis)',
        },
    ],
};
//# sourceMappingURL=product.descriptor.js.map